/**
 * The front desk.
 *
 * One screen, because a receptionist with a queue in front of them does not
 * navigate: they pick today's patient, type, and hand over a link. Everything is
 * on the right of the split, the day's list is on the left, and nothing takes a
 * page load.
 *
 * The custom-field row is the part worth reading. The requirement is that a
 * receptionist can invent a labelled input mid-shift — "ضغط مرتفع؟" as a tick,
 * plus a details box — without waiting for anybody. Creating one posts to
 * `/reception/fields`, which is idempotent on the label, so typing the same
 * field name twice reuses it rather than making a second field that answers into
 * the same slot.
 *
 * A new field is **not public by default**. The checkbox that publishes it to
 * the patient's link is separate and off, because a receptionist inventing
 * "ملاحظات داخلية" must not discover later that it was published to a URL
 * anybody holding the link can open.
 *
 * The 14-dashboard upgrade adds three tabs beside the day's bookings, because
 * the requirement gives the desk the same data entry as Contracts:
 *
 *   ملفات المرضى   register an insured or a cash patient (four-part name, at
 *                  least one identifier, chronic diseases, medication…), set
 *                  their cover, file their forms, route them to a department
 *                  (checkup, lab, radiology, pharmacy, rooms, surgery) with an
 *                  emergency / normal / waitlist flag, and hand them the portal
 *   التحصيل        the balances Accounts pushed to the desk, collected against
 *                  an electronic receipt with a unique number
 *   إيصال جديد     a receipt for anything else — "إذن تحصيل أجل" by default
 *   تقرير الوردية  the shift's receipts, the clerk's own or everybody's,
 *                  summed and printed for the handover (QA report §4.2)
 *   موافقة التحاليل والأشعة  the money behind a lab or scan request, cleared
 *                  at the counter (DIAGNOSTICS_APPROVE) — Contracts keeps the rest
 *   تأكيد العمليات عن الجراح  a surgeon's yes, recorded for them after the desk
 *                  confirmed it by phone (SURGERY_CONFIRM_ON_BEHALF)
 *
 * المرحلة 1.35 adds «الاستقبال السريع» (QuickReception.jsx): the centre's
 * paper voucher on one card, first, where the institution's quickReception
 * layout is quick or both and its price list is on. Under «quick» it stands
 * with the file, the collections and the shift report only — the day's
 * bookings and the free receipt are the classic desk's.
 *
 * A booking on the day's list links to a hospital file, so the doctor's panel,
 * the secretary and the file all talk about the same patient.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  Panel,
  Select,
  Spinner,
  Tabs,
  Textarea,
  DateInput,
} from '../../components/common';
import { useAsync } from '../../hooks/useAsync';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { operationsApi, patientsApi, receiptsApi, receptionApi, requestsApi } from '../../api/endpoints';
import { HeroBand, StepStrip, Tile3D } from '../../components/common/Tile3D';
import { PERMISSIONS } from '../../domain/rbac';
import { STATUS_LABELS, STATUS_STYLES } from '../../domain/bookings';
import { todayKey } from '../../domain/dates';
import { PatientFinder } from '../hospitalShared/PatientFinder';
import { PatientWorkspace } from '../hospitalShared/PatientWorkspace';
import { ReceiptDialog, ReceiptPreview } from '../hospitalShared/ReceiptSheet';
import { ReceiptLookup } from '../hospitalShared/ReceiptLookup';
import { formatDateTime, formatMoney } from '../hospitalShared/labels';
import { LiveQueueBoard } from '../queue/LiveQueueBoard';
import { WhatsAppLink } from '../../components/common/WhatsAppLink';
import { ShiftReport } from '../hospitalShared/ShiftReport';
import { FinanceQueue } from '../hospitalShared/FinanceApprovals';
import { SurgeonConfirmQueue } from '../hospitalShared/SurgeryDecision';
import { ClinicDaySheet } from '../operations/ClinicDaySheet';
import { useI18n } from '../../i18n/I18nProvider';
import { centerPolicy } from '../../domain/centerPolicy';
import { QuickReception } from './QuickReception';
import { VoucherPreview } from './VoucherPreview';

/** The file fields an intake can fill, as the desk reads them. */
const FILLED_LABELS = {
  nationalId: 'الرقم القومي',
  phonePrimary: 'الهاتف',
  phoneSecondary: 'الهاتف الثاني',
  insuranceProvider: 'جهة التأمين',
  insuranceCode: 'كود التأمين',
  complaints: 'الشكوى',
};

/**
 * The booking's hospital file: which one it is linked to, or a way to link it.
 *
 * Linking is what lets the doctor's panel open this patient's whole file from
 * the appointment, and what lets the desk route them from here.
 */
function HospitalFileLink({ booking, onOpen }) {
  const { can } = usePermissions();
  const { success, error: toastError } = useNotifications();
  const [state, setState] = useState({ loading: true, patient: null });
  const [picking, setPicking] = useState(false);

  const load = useCallback(() => {
    setState({ loading: true, patient: null });
    patientsApi
      .byBooking(booking.id)
      .then((r) => setState({ loading: false, patient: r.patient }))
      .catch(() => setState({ loading: false, patient: null }));
  }, [booking.id]);

  useEffect(() => {
    load();
  }, [load]);

  const link = async (patient) => {
    try {
      await patientsApi.linkBooking(patient.id, booking.id);
      success(`تم ربط الحجز بملف ${patient.fullName}`);
      setPicking(false);
      load();
    } catch (err) {
      toastError(err.message);
    }
  };

  if (state.loading) return null;

  return (
    <Panel>
      {state.patient ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="tile3d__face tone-purple !h-10 !w-10 shrink-0" aria-hidden="true">
            <Icon name="file-heart" size={18} className="tile3d__icon" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-slate-400">ملف المستشفى</p>
            <p className="truncate font-bold text-white">{state.patient.fullName}</p>
          </div>
          <Button icon="folder-open" onClick={() => onOpen(state.patient)}>
            فتح الملف والتوجيه
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Icon name="unlink" size={18} className="text-amber-300" />
          <p className="min-w-0 flex-1 text-sm text-slate-300">هذا الحجز غير مربوط بملف مريض في المستشفى.</p>
          {can(PERMISSIONS.PATIENT_FILE_MANAGE) && (
            <Button variant="ghost" icon="link" onClick={() => setPicking(true)}>
              ربط بملف
            </Button>
          )}
        </div>
      )}
      <Modal open={picking} onClose={() => setPicking(false)} title="ربط الحجز بملف مريض" icon="link" size="lg">
        <PatientFinder
          onSelect={link}
          onCreateNew={() => {
            setPicking(false);
            onOpen(null, booking.id);
          }}
        />
      </Modal>
    </Panel>
  );
}

/** The desk's till: what Accounts sent, grouped by patient, and today's receipts. */
function DeskCollections() {
  const { can } = usePermissions();
  const { success, error: toastError } = useNotifications();
  const [due, setDue] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [error, setError] = useState(null);
  const [settling, setSettling] = useState(null);
  const [viewing, setViewing] = useState(null);
  // The full receipt, so a reprint carries its queue ticket (QA report §5.3);
  // the row already in hand is shown if that read fails.
  const openReceipt = async (rc) => setViewing(await receiptsApi.get(rc.id).catch(() => rc));

  const load = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([requestsApi.list({ queue: 'desk' }), receiptsApi.list({ date: todayKey() })]);
      setDue(d);
      setReceipts(r);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const collect = async (receipt) => {
    try {
      await receiptsApi.collect(receipt.id);
      success(`تم تحصيل ${receipt.number}`);
      load();
    } catch (err) {
      toastError(err.message);
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!due || !receipts) return <Spinner label="جارٍ تحميل المستحقات" variant="inline" />;

  const byPatient = due.reduce((map, r) => {
    const key = r.hospitalPatientId;
    (map[key] = map[key] || { patient: { id: key, ...(r.patient || {}) }, rows: [] }).rows.push(r);
    return map;
  }, {});
  const groups = Object.values(byPatient);
  // A refund («مرتجع») comes off what the day collected.
  const todayTotal = receipts.filter((r) => r.status === 'collected').reduce((s, r) => s + Number(r.total || 0) - Number(r.refunded || 0), 0);

  return (
    <div className="space-y-6">
      <section>
        <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="hand-coins" size={18} className="text-amber-300" /> مستحقات أرسلتها الحسابات ({groups.length})
        </h2>
        {groups.length === 0 ? (
          <EmptyState icon="hand-coins" title="لا توجد مبالغ مستحقة الآن" />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {groups.map((g) => {
              const total = g.rows.reduce((s, r) => s + Number(r.amountDue || 0), 0);
              return (
                <div key={g.patient.id} className="glass-panel rounded-2xl border border-amber-500/20 p-4">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="font-bold text-white">{g.patient.fullName}</p>
                    <Badge className="border-amber-500/40 bg-amber-500/15 text-amber-100">{formatMoney(total)}</Badge>
                  </div>
                  <ul className="mb-3 space-y-1 text-sm text-slate-300">
                    {g.rows.map((r) => (
                      <li key={r.id} className="flex justify-between gap-2">
                        <span>
                          {r.departmentLabel}: {r.title}
                        </span>
                        <span>{formatMoney(r.amountDue)}</span>
                      </li>
                    ))}
                  </ul>
                  {can(PERMISSIONS.RECEIPT_ISSUE) && (
                    <Button icon="receipt" onClick={() => setSettling(g)}>
                      تحصيل وإصدار إيصال
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-white">
            <Icon name="receipt" size={18} className="text-cyan-300" /> إيصالات اليوم ({receipts.length})
          </h2>
          <Badge className="border-emerald-500/40 bg-emerald-500/15 text-emerald-100">حُصّل اليوم {formatMoney(todayTotal)}</Badge>
        </div>
        {receipts.length === 0 ? (
          <p className="text-sm text-slate-500">لم يصدر إيصال اليوم بعد.</p>
        ) : (
          <ul className="divide-y divide-white/5 rounded-xl border border-white/5">
            {receipts.map((rc) => (
              <li key={rc.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
                <span className="font-mono text-cyan-200" dir="ltr">
                  {rc.number}
                </span>
                <span className="text-slate-200">{rc.patient?.fullName}</span>
                <span className="text-xs text-slate-500">{formatDateTime(rc.issuedAt)}</span>
                <Badge
                  className={
                    rc.status === 'collected'
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                      : rc.status === 'void'
                        ? 'border-slate-500/30 bg-slate-500/10 text-slate-400'
                        : 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                  }
                >
                  {rc.status === 'collected' ? 'محصّل' : rc.status === 'void' ? 'ملغي' : 'آجل'}
                </Badge>
                {Array.isArray(rc.refunds) && rc.refunds.length > 0 && (
                  <Badge className="border-rose-500/30 bg-rose-500/10 text-rose-200">مرتجع −{formatMoney(rc.refunded)}</Badge>
                )}
                <span className="ms-auto font-bold text-white">{formatMoney(rc.total)}</span>
                {rc.status === 'issued' && can(PERMISSIONS.RECEIPT_ISSUE) && (
                  <Button variant="success" icon="check" onClick={() => collect(rc)}>
                    تم التحصيل
                  </Button>
                )}
                <Button variant="subtle" icon={rc.template === 'voucher' ? 'receipt' : 'printer'} onClick={() => openReceipt(rc)} data-testid="desk-receipt-open">
                  {rc.template === 'voucher' ? 'فتح: طباعة وواتساب ومرتجع' : 'طباعة'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {settling && (
        <ReceiptDialog open patient={settling.patient} requests={settling.rows} onClose={() => setSettling(null)} onIssued={load} />
      )}
      <Modal open={Boolean(viewing)} onClose={() => setViewing(null)} title={viewing?.number} icon="receipt" size={viewing?.template === 'voucher' ? 'xl' : 'lg'}>
        {viewing &&
          (viewing.template === 'voucher' ? (
            <VoucherPreview
              receipt={viewing}
              onChanged={(updated) => {
                setViewing(updated);
                load();
              }}
            />
          ) : (
            <ReceiptPreview receipt={viewing} />
          ))}
      </Modal>
    </div>
  );
}

/** Registering, covering, routing and linking — the patient files tab. */
function PatientFiles({ initial, onInitialUsed }) {
  const { serviceShown } = usePermissions();
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(null);
  // Which tab the workspace opens on, and — when the file was opened from a
  // scanned receipt — that receipt, so the desk can see what it was for.
  const [initialSection, setInitialSection] = useState(null);
  const [fromReceipt, setFromReceipt] = useState(null);
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!initial) return;
    if (initial.patient) {
      setSelected(initial.patient);
      setCreating(null);
    } else {
      setSelected(null);
      setCreating({ bookingId: initial.bookingId || null });
    }
    setInitialSection(initial.section || null);
    setFromReceipt(initial.receipt || null);
    setRound((r) => r + 1);
    onInitialUsed();
  }, [initial, onInitialUsed]);

  const jump = (section) => {
    setInitialSection(section);
    setRound((r) => r + 1);
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[22rem_1fr]">
      <Panel className="h-fit">
        <PatientFinder
          selectedId={selected?.id}
          onSelect={(p) => {
            setSelected(p);
            setCreating(null);
            setInitialSection(null);
            setFromReceipt(null);
          }}
          onCreateNew={() => {
            setSelected(null);
            setCreating({ bookingId: null });
            setInitialSection(null);
            setFromReceipt(null);
          }}
        />
      </Panel>
      <Panel className="min-w-0">
        {fromReceipt && selected && (
          <div className="found-banner">
            <Icon name="scan-barcode" size={20} className="text-cyan-300" />
            <p className="min-w-0 flex-1 text-sm text-slate-200">
              فُتح الملف من الإيصال <b className="font-mono text-white" dir="ltr">{fromReceipt.number}</b>
            </p>
            {/* «رفع الروشتة» و«تحديد الخدمات» — one key in the package. */}
            {serviceShown('rxUpload') && (
              <>
                <Button variant="subtle" icon="paperclip" onClick={() => jump('attachments')}>
                  رفع الروشتة
                </Button>
                <Button variant="subtle" icon="ticket-plus" onClick={() => jump('visit')}>
                  تحديد الخدمات
                </Button>
              </>
            )}
          </div>
        )}
        {selected || creating ? (
          <PatientWorkspace
            key={`${selected?.id || `new-${creating?.bookingId || ''}`}-${round}`}
            patient={selected}
            bookingId={creating?.bookingId || null}
            sections={['visit', 'file', 'child', 'coverage', 'attachments', 'route', 'medical', 'portal', 'receipts']}
            defaultSection={creating ? 'file' : initialSection || 'visit'}
            onPatientChange={(p) => {
              // A file just registered goes straight to its visit: what the
              // patient came for, and the receipt that sends them there.
              if (creating) {
                setInitialSection('visit');
                setRound((r) => r + 1);
              }
              setSelected(p);
              setCreating(null);
            }}
          />
        ) : (
          <EmptyState
            icon="user-plus"
            title="اختر مريضاً أو سجّل ملفاً جديداً"
            message="مريض تأمين أو نقدي — سجّل بياناته مرة واحدة، ثم وجّهه للكشف أو التحاليل أو الأشعة أو الغرف."
          />
        )}
      </Panel>
    </div>
  );
}

/** A receipt for a walk-in: pick the patient if they have a file, or type the name. */
function FreeReceipt() {
  const [patient, setPatient] = useState(undefined);
  const [open, setOpen] = useState(false);
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[22rem_1fr]">
      <Panel className="h-fit">
        <PatientFinder
          canCreate={false}
          selectedId={patient?.id}
          onSelect={(p) => {
            setPatient(p);
            setOpen(true);
          }}
        />
      </Panel>
      <Panel>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Tile3D
            icon="receipt"
            label="إيصال لمريض بدون ملف"
            hint="اكتب الاسم والخدمات والمبالغ"
            tone="cyan"
            enter
            onClick={() => {
              setPatient(null);
              setOpen(true);
            }}
          />
          <Tile3D icon="search" label="أو اختر مريضاً من القائمة" hint="تُملأ بياناته تلقائياً" tone="purple" enter delay={1} />
        </div>
      </Panel>
      {open && <ReceiptDialog open patient={patient || null} onClose={() => setOpen(false)} />}
    </div>
  );
}

const emptyIntake = () => ({
  nationalId: '',
  phonePrimary: '',
  phoneSecondary: '',
  insuranceProvider: '',
  insuranceNo: '',
  chiefComplaint: '',
  customFields: [],
});

export function ReceptionDesk() {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const { can, serviceShown, locale } = usePermissions();
  // المرحلة 1.35: the quick card, where the policy and the price list allow it.
  const center = centerPolicy(locale);
  const quickLayout = center.priceList.enabled === 'yes' && can(PERMISSIONS.RECEIPT_ISSUE) ? center.quickReception.layout : 'classic';

  const [date, setDate] = useState(todayKey());
  const [selected, setSelected] = useState(null);
  const [intake, setIntake] = useState(emptyIntake());
  const [saving, setSaving] = useState(false);
  const [share, setShare] = useState(null);
  const [newField, setNewField] = useState(null);
  const [picked, setTab] = useState(null);
  const [fileRequest, setFileRequest] = useState(null);
  const clearFileRequest = useCallback(() => setFileRequest(null), []);

  /** Open a hospital file from a booking — or start a new one linked to it. */
  const openFile = useCallback((patient, bookingId = null, section = null, receipt = null) => {
    setFileRequest({ patient, bookingId, section, receipt });
    setTab('patients');
  }, []);

  const board = useAsync(() => operationsApi.board(date), [date]);
  const fields = useAsync(() => receptionApi.fields(), []);

  const bookings = useMemo(() => {
    const list = board.data?.bookings || [];
    return [...list].sort((a, b) => String(a.time).localeCompare(String(b.time)));
  }, [board.data]);

  const doctorName = useCallback(
    (id) => (board.data?.doctors || []).find((d) => d.id === id)?.name || '—',
    [board.data],
  );

  /* Load the selected booking's intake, and whatever link it already has. */
  useEffect(() => {
    if (!selected) return undefined;
    let cancelled = false;

    setIntake(emptyIntake());
    setShare(null);

    receptionApi
      .intake(selected.id)
      .then((result) => {
        if (cancelled) return;
        if (result.intake) {
          setIntake({
            nationalId: result.intake.nationalId || '',
            phonePrimary: result.intake.phonePrimary || '',
            phoneSecondary: result.intake.phoneSecondary || '',
            insuranceProvider: result.intake.insuranceProvider || '',
            insuranceNo: result.intake.insuranceNo || '',
            chiefComplaint: result.intake.chiefComplaint || '',
            customFields: result.intake.customFields || [],
          });
        }
      })
      .catch((err) => !cancelled && toastError(err.message));

    receptionApi
      .shareLink(selected.id)
      .then((r) => !cancelled && setShare(r.link))
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [selected, toastError]);

  const answerFor = (key) =>
    intake.customFields.find((f) => f.key === key)?.value ?? '';

  const setAnswer = (definition, value) => {
    setIntake((current) => {
      const rest = current.customFields.filter((f) => f.key !== definition.key);
      return {
        ...current,
        customFields: [
          ...rest,
          { key: definition.key, label: definition.label, type: definition.type, value },
        ],
      };
    });
  };

  const saveIntake = useCallback(async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const saved = await receptionApi.saveIntake(selected.id, intake);
      const filled = (saved?.fileFilled || []).map((key) => FILLED_LABELS[key] || key);
      // The intake fills the linked file's blanks (QA report §1.2) — say
      // which, so the desk knows every other department now sees them too.
      success(filled.length ? `تم الحفظ وأُضيف إلى الملف الطبي: ${filled.join('، ')}` : 'تم حفظ بيانات الاستقبال');
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  }, [selected, intake, success, toastError]);

  /** Save, then mint. The link renders the intake, so an unsaved form would
   *  produce a link showing the previous answers — which is worse than none. */
  const saveAndShare = useCallback(async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await receptionApi.saveIntake(selected.id, intake);
      // No duration: the server applies the institution's «مدة الرابط».
      const link = await receptionApi.issueShare(selected.id);
      setShare({ ...link, justIssued: true });
      success('تم إنشاء رابط المريض');
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  }, [selected, intake, success, toastError]);

  const createField = useCallback(async () => {
    try {
      await receptionApi.createField(newField);
      setNewField(null);
      fields.reload();
      success('تمت إضافة الحقل');
    } catch (err) {
      toastError(err.message);
    }
  }, [newField, fields, success, toastError]);

  if (board.error) return <ErrorState error={board.error} onRetry={board.reload} />;

  const mayIssue = can(PERMISSIONS.SHARE_LINK_ISSUE);
  const mayAppend = can(PERMISSIONS.PATIENT_RECORD_APPEND);
  // The central control can take a tab away while it is open (the package or
  // the institution's policy changed): the desk falls back to the day's bookings.
  const allTabs = [
    ...(quickLayout !== 'classic' ? [{ id: 'quick', label: t('quick.tab'), icon: 'receipt' }] : []),
    { id: 'bookings', label: 'حجوزات اليوم', icon: 'calendar-check', count: bookings.length },
    ...(can(PERMISSIONS.PATIENT_FILE_MANAGE) || can(PERMISSIONS.PATIENT_RECORD_VIEW)
      ? [{ id: 'patients', label: 'ملفات المرضى والتوجيه', icon: 'user-plus' }]
      : []),
    ...(can(PERMISSIONS.RECEIPT_ISSUE)
      ? [
          { id: 'desk', label: 'التحصيل والإيصالات', icon: 'hand-coins' },
          { id: 'receipt', label: 'إيصال جديد', icon: 'receipt' },
          // «تقرير الوردية» is a key of its own in the package.
          ...(serviceShown('shiftReport') ? [{ id: 'shift', label: 'تقرير الوردية', icon: 'clipboard-check' }] : []),
        ]
      : []),
    // Labs and scans are cleared at the counter (DIAGNOSTICS_APPROVE),
    // and the desk that phones a surgeon records their yes for them.
    ...(can(PERMISSIONS.DIAGNOSTICS_APPROVE) ? [{ id: 'approvals', label: 'موافقة التحاليل والأشعة', icon: 'shield-check' }] : []),
    ...(can(PERMISSIONS.SURGERY_CONFIRM_ON_BEHALF) ? [{ id: 'surgeries', label: 'تأكيد العمليات عن الجراح', icon: 'phone-call' }] : []),
    // The desk issues the turns (every receipt with a checkup takes one)
    // and watches the clinics move; moving them is clinics reception's.
    ...(serviceShown('queue') ? [{ id: 'queue', label: 'الطابور المباشر', icon: 'list-ordered' }] : []),
    // Who is in which clinic today — the sheet the front desk prints.
    ...(serviceShown('daySheet') ? [{ id: 'daysheet', label: 'جدول العيادات', icon: 'calendar-days' }] : []),
  ];
  // Under «quick» the card stands with the file and the money only.
  const tabs = quickLayout === 'quick' ? allTabs.filter((x) => ['quick', 'patients', 'desk', 'shift'].includes(x.id)) : allTabs;
  const tab = tabs.some((x) => x.id === picked) ? picked : tabs[0].id;
  const openFileById = (id) => patientsApi.get(id).then((p) => openFile(p)).catch((err) => toastError(err.message));

  return (
    <div>
      <HeroBand
        icon="clipboard-list"
        tone="cyan"
        title="استقبال المرضى"
        subtitle="سجّل المريض، اختر العيادة والطبيب، واصدر الإيصال — يصل فوراً لاستقبال العيادات."
        compact={tab === 'quick'}
      >
        <ReceiptLookup onFound={({ patient, receipt }) => openFile(patient, null, 'visit', receipt)} />
        {tab === 'bookings' && (
          <DateInput
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-auto"
          />
        )}
      </HeroBand>

      <Tabs
        tabs={tabs}
        active={tab}
        onChange={setTab}
      />

      {tab === 'quick' && <QuickReception onOpenFile={openFileById} />}
      {tab === 'patients' && <PatientFiles initial={fileRequest} onInitialUsed={clearFileRequest} />}
      {tab === 'desk' && <DeskCollections />}
      {tab === 'receipt' && <FreeReceipt />}
      {tab === 'shift' && <ShiftReport defaultScope="me" />}
      {tab === 'queue' && <LiveQueueBoard />}
      {tab === 'approvals' && (
        <FinanceQueue
          onOpenPatient={(id) => patientsApi.get(id).then((p) => openFile(p)).catch(() => {})}
          emptyHint="تظهر هنا طلبات التحاليل والأشعة التي تنتظر الموافقة المالية. العمليات والإقامة والأدوية توافق عليها إدارة التعاقدات."
        />
      )}
      {tab === 'surgeries' && <SurgeonConfirmQueue />}
      {tab === 'daysheet' && <ClinicDaySheet initialDate={date} />}

      {tab === 'bookings' && (
      <>
      {/* Three steps, always visible. This job is done dozens of times a day by
          somebody who was shown it once. */}
      <StepStrip
        steps={['اختر المريض', 'سجّل البيانات', 'أرسل الرابط']}
        current={!selected ? 0 : share ? 2 : 1}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_1fr]">
        {/* The day */}
        <Panel padded={false} className="h-fit">
          <div className="border-b border-white/5 p-4">
            <h2 className="text-sm font-bold text-white">
              حجوزات اليوم
              <span className="ms-2 text-slate-400">({bookings.length})</span>
            </h2>
          </div>

          {board.loading ? (
            <div className="p-6">
              <Spinner label="جارٍ التحميل" variant="panel" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="p-6">
              <EmptyState icon="calendar-x" title="لا توجد حجوزات" message="اختر تاريخاً آخر." />
            </div>
          ) : (
            <ul className="custom-scrollbar max-h-[70vh] divide-y divide-white/5 overflow-y-auto">
              {bookings.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(b)}
                    className={`flex w-full items-center gap-3 p-3 text-start transition-colors hover:bg-white/5 ${
                      selected?.id === b.id ? 'bg-cyan-500/10' : ''
                    }`}
                  >
                    <span className="shrink-0 rounded-lg bg-slate-800 px-2 py-1 font-mono text-xs text-cyan-300">
                      {b.time}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold text-white">
                        {b.patientName}
                      </span>
                      <span className="block truncate text-xs text-slate-400">
                        {doctorName(b.doctorId)}
                      </span>
                    </span>
                    {b.arrivalFrom && (
                      <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
                        {b.arrivalFrom}
                      </Badge>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* The intake */}
        {!selected ? (
          <Panel>
            <EmptyState
              icon="user-check"
              title="اختر مريضاً"
              message="اختر حجزاً من قائمة اليوم لبدء تسجيل بياناته."
            />
          </Panel>
        ) : (
          <div className="space-y-5">
            <HospitalFileLink key={selected.id} booking={selected} onOpen={openFile} />

            <Panel>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-white">{selected.patientName}</h2>
                  <p className="text-sm text-slate-400">
                    {selected.time} · {doctorName(selected.doctorId)}
                  </p>
                </div>
                <Badge className={STATUS_STYLES[selected.status] || 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300'}>
                  {STATUS_LABELS[selected.status] || selected.status}
                </Badge>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="الرقم القومي">
                  <Input
                    dir="ltr"
                    value={intake.nationalId}
                    onChange={(e) => setIntake({ ...intake, nationalId: e.target.value })}
                  />
                </Field>
                <Field label="رقم الهاتف">
                  <Input
                    dir="ltr"
                    value={intake.phonePrimary}
                    onChange={(e) => setIntake({ ...intake, phonePrimary: e.target.value })}
                  />
                </Field>
                <Field label="رقم هاتف بديل">
                  <Input
                    dir="ltr"
                    value={intake.phoneSecondary}
                    onChange={(e) => setIntake({ ...intake, phoneSecondary: e.target.value })}
                  />
                </Field>
                <Field label="شركة التأمين">
                  <Input
                    value={intake.insuranceProvider}
                    onChange={(e) => setIntake({ ...intake, insuranceProvider: e.target.value })}
                  />
                </Field>
                <Field label="رقم التأمين">
                  <Input
                    dir="ltr"
                    value={intake.insuranceNo}
                    onChange={(e) => setIntake({ ...intake, insuranceNo: e.target.value })}
                  />
                </Field>
              </div>

              <Field label="الشكوى الرئيسية" className="mt-4">
                <Textarea
                  rows={3}
                  value={intake.chiefComplaint}
                  onChange={(e) => setIntake({ ...intake, chiefComplaint: e.target.value })}
                />
              </Field>
            </Panel>

            {/* Custom fields */}
            <Panel>
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="text-sm font-bold text-white">حقول إضافية</h3>
                {can(PERMISSIONS.INTAKE_FIELDS_MANAGE) && (
                  <Button
                    variant="ghost"
                    icon="plus"
                    onClick={() =>
                      setNewField({ label: '', type: 'text', options: [], publicVisible: false, preVisit: 'after' })
                    }
                  >
                    حقل جديد
                  </Button>
                )}
              </div>

              {(fields.data || []).length === 0 ? (
                <p className="text-sm text-slate-400">
                  لا توجد حقول إضافية بعد — أضف حقلاً ليظهر في كل استقبال.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {(fields.data || []).map((definition) => (
                    <Field
                      key={definition.id}
                      label={definition.label}
                      hint={
                        [definition.publicVisible ? 'يظهر للمريض في الرابط' : null, definition.preVisit && definition.preVisit !== 'after' ? t(`preVisit.field.${definition.preVisit}`) : null]
                          .filter(Boolean)
                          .join(' · ') || undefined
                      }
                    >
                      {definition.type === 'boolean' ? (
                        <Checkbox
                          label="نعم"
                          checked={Boolean(answerFor(definition.key))}
                          onChange={(e) => setAnswer(definition, e.target.checked)}
                        />
                      ) : definition.type === 'select' ? (
                        <Select
                          value={answerFor(definition.key)}
                          onChange={(e) => setAnswer(definition, e.target.value)}
                        >
                          <option value="">—</option>
                          {(definition.options || []).map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          type={definition.type === 'number' ? 'number' : 'text'}
                          value={answerFor(definition.key)}
                          onChange={(e) => setAnswer(definition, e.target.value)}
                        />
                      )}
                    </Field>
                  ))}
                </div>
              )}
            </Panel>

            {/* Save and share */}
            <Panel>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Tile3D
                  icon="save"
                  label="حفظ البيانات"
                  hint="بدون إرسال رابط"
                  tone="slate"
                  enter
                  delay={0}
                  disabled={saving || !mayAppend}
                  onClick={saveIntake}
                />
                {mayIssue && (
                  <Tile3D
                    icon="qr-code"
                    label="حفظ وإرسال الرابط"
                    hint="رابط + رمز QR للمريض"
                    tone="emerald"
                    pulse
                    enter
                    delay={1}
                    disabled={saving}
                    onClick={saveAndShare}
                  />
                )}
              </div>

              {share && <SharePanel share={share} />}
            </Panel>
          </div>
        )}
      </div>
      </>
      )}

      <Modal
        open={Boolean(newField)}
        onClose={() => setNewField(null)}
        title="حقل استقبال جديد"
        footer={
          <>
            <Button variant="ghost" onClick={() => setNewField(null)}>
              إلغاء
            </Button>
            <Button onClick={createField} disabled={!newField?.label?.trim()}>
              إضافة
            </Button>
          </>
        }
      >
        {newField && (
          <div className="space-y-4">
            <Field label="اسم الحقل" required hint="مثال: ضغط مرتفع؟">
              <Input
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value })}
              />
            </Field>
            <Field label="النوع">
              <Select
                value={newField.type}
                onChange={(e) => setNewField({ ...newField, type: e.target.value })}
              >
                <option value="text">نص</option>
                <option value="boolean">نعم / لا</option>
                <option value="number">رقم</option>
                <option value="select">اختيار من قائمة</option>
              </Select>
            </Field>
            {newField.type === 'select' && (
              <Field label="الخيارات" hint="افصل بينها بفاصلة">
                <Input
                  onChange={(e) =>
                    setNewField({
                      ...newField,
                      options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })
                  }
                />
              </Field>
            )}
            {/* المرحلة 1.2: when the patient is asked it; «after» is today. */}
            <Field label={t('preVisit.field.when')} hint={t('preVisit.field.whenHint')}>
              <Select value={newField.preVisit || 'after'} onChange={(e) => setNewField({ ...newField, preVisit: e.target.value })} data-testid="intake-field-previsit">
                {['after', 'before', 'optional'].map((value) => (
                  <option key={value} value={value}>
                    {t(`preVisit.field.${value}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Checkbox
              label="إظهار هذا الحقل للمريض في رابط الزيارة"
              checked={newField.publicVisible}
              onChange={(e) => setNewField({ ...newField, publicVisible: e.target.checked })}
            />
            <p className="text-xs text-slate-400">
              الحقول الجديدة لا تظهر للمريض إلا إذا حددت ذلك صراحةً.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

/** The minted link, its QR, and the one tap that sends it. */
function SharePanel({ share }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* a clipboard the browser refuses is not worth an error toast */
    }
  };

  return (
    <div className="mt-5 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
      <div className="flex flex-wrap items-start gap-4">
        {share.qrSvg && (
          <div
            className="h-32 w-32 shrink-0 rounded-xl bg-white p-2"
            /* The SVG comes from our own backend's qrcode render, not from user
               input — but it is still injected rather than parsed, so it is
               worth naming why that is acceptable here and nowhere else. */
            dangerouslySetInnerHTML={{ __html: share.qrSvg }}
          />
        )}

        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <p className="mb-1 flex items-center gap-1.5 text-xs font-bold text-emerald-300">
              <Icon name="link" size={13} /> رابط المريض
            </p>
            <p dir="ltr" className="break-all font-mono text-xs text-slate-300">
              {share.url}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={copy} icon={copied ? 'check' : 'copy'}>
              {copied ? 'تم النسخ' : 'نسخ'}
            </Button>
          </div>
          {share.whatsappUrl && <WhatsAppLink url={share.whatsappUrl} preview testId="visit-whatsapp" />}

          <p className="text-[11px] text-slate-400">
            إنشاء رابط جديد يُلغي الرابط السابق فوراً.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ReceptionDesk;
